import { PermissionEnum } from '@Application/Security/Roles';
import SuperAdminTag from '@UI/Pages/Administration/AiCosts/CostTabs/SuperAdminTag';
import PillTabView from '@Guidelines/Component/PillTabView';
import CallCosts from '@UI/Pages/Administration/AiCosts/CostTabs/CallCosts';
import ItemCosts from '@UI/Pages/Administration/AiCosts/CostTabs/ItemCosts';
import StepCosts from '@UI/Pages/Administration/AiCosts/CostTabs/StepCosts';
import SubjectCosts from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts';
import Filters from '@UI/Pages/Administration/AiCosts/Filters';
import type { AiCostsTab } from '@UI/Pages/Administration/AiCosts/Filters/AiCostsFilterView';
import aiCostsFiltersSignal, {
	setAiCostsFilters,
} from '@UI/Pages/Administration/AiCosts/Filters/Signals/aiCostsFiltersSignal';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { TabPanel, type TabViewTabChangeEvent } from '@verclam/ui-kit';

type TabEntry = {
	readonly tab: AiCostsTab;
	readonly header: React.ReactNode;
};

const SUPER_ADMIN_BADGE = <SuperAdminTag />;

const COMMON_TABS: readonly TabEntry[] = [
	{ tab: 'facilities', header: 'Par agence' },
	{ tab: 'organizations', header: 'Par structure' },
	{ tab: 'users', header: 'Par utilisateur' },
	{ tab: 'documents', header: 'Par document' },
	{ tab: 'interviews', header: 'Par audio' },
];

const SUPER_ADMIN_TABS: readonly TabEntry[] = [
	{
		tab: 'steps',
		header: <React.Fragment>Par étape {SUPER_ADMIN_BADGE}</React.Fragment>,
	},
	{
		tab: 'calls',
		header: <React.Fragment>Appels {SUPER_ADMIN_BADGE}</React.Fragment>,
	},
];

type Props = {
	readonly accountId: number;
};

type TabContentProps = {
	readonly accountId: number;
	readonly tab: AiCostsTab;
};

const TabContent: React.FC<TabContentProps> = ({ accountId, tab }) => {
	switch (tab) {
		case 'facilities':
		case 'organizations':
		case 'users':
			return <SubjectCosts accountId={accountId} dimension={tab} />;
		case 'documents':
		case 'interviews':
			return <ItemCosts accountId={accountId} dimension={tab} />;
		case 'steps':
			return <StepCosts accountId={accountId} />;
		case 'calls':
			return <CallCosts accountId={accountId} />;
	}
};

/** The tabs write the active tab to the filters signal; the filters and the table of the active tab are drawn under them. */
const CostTabs: React.FC<Props> = ({ accountId }) => {
	const filters = useSignalValue(aiCostsFiltersSignal);
	const isSuperAdministrator = useHasPermission(PermissionEnum.SYSTEM_CONFIG);

	const tabs = React.useMemo(
		() => (isSuperAdministrator ? [...COMMON_TABS, ...SUPER_ADMIN_TABS] : COMMON_TABS),
		[isSuperAdministrator],
	);

	const activeIndex = React.useMemo(
		() => tabs.findIndex((entry) => entry.tab === filters.tab),
		[tabs, filters.tab],
	);

	const handleTabChange = React.useCallback(
		(event: TabViewTabChangeEvent) => {
			const entry = tabs[event.index];

			if (entry !== undefined) {
				setAiCostsFilters(aiCostsFiltersSignal.peek().setTab(entry.tab));
			}
		},
		[tabs],
	);

	// A reader never sees its key change: a new period, filter or tab remounts it, and the store decides whether a request goes out.
	const tabKey = `${filters.tab}:${filters.tabQueryParams.toString()}`;

	return (
		<React.Fragment>
			<PillTabView activeIndex={activeIndex} onTabChange={handleTabChange} scrollable>
				{tabs.map((entry) => (
					<TabPanel key={entry.tab} header={entry.header} />
				))}
			</PillTabView>
			<Filters accountId={accountId} />
			<TabContent key={tabKey} accountId={accountId} tab={filters.tab} />
		</React.Fragment>
	);
};

export default CostTabs;
